import { useMemo, useCallback, useState } from "react";

import { getZcCollateral, getZcLedger, AbfLoanExpanded } from "@bridgesplit/abf-react";
import { roundAutoToDecimals } from "@bridgesplit/utils";
import { WindType } from "@loopscale/pandora-types";
import { useBalanceChecker } from "app/components/common";

import { createRepaymentParams, useRepay } from "../util";
import { UsePrincipalRepaymentReturn } from "./types";

export function usePrincipalRepayment({
    loanExpanded,
    collateralWithdrawAmount
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    collateralWithdrawAmount?: number;
}): UsePrincipalRepaymentReturn {
    const [inputAmount, setInputAmount] = useState<number | undefined>(undefined);
    const [isFullRepay, setIsFullRepay] = useState(false);

    const ledger = getZcLedger(loanExpanded);
    const collateral = getZcCollateral(loanExpanded);
    const newCollateralAmount = collateral?.amount ?? 0;

    // Principal-specific data
    const totalDue = ledger?.ledgerDebt.total ?? 0;
    const effectiveInputAmount = isFullRepay ? totalDue : Math.min(inputAmount ?? 0, totalDue);
    const principalMetadataDecimals = loanExpanded?.principalMetadata.decimals;

    // Balance checking for principal token
    const {
        balance,
        insufficientBalance,
        isLoading: balanceLoading
    } = useBalanceChecker({
        mint: loanExpanded?.principalMetadata.mint,
        amount: effectiveInputAmount,
        skip: false
    });

    const maxRepayable = Math.min(balance ?? 0, totalDue);

    const params = createRepaymentParams({
        principalPaymentUiAmount: effectiveInputAmount,
        collateralPaymentUiAmount: collateralWithdrawAmount,
        isFullRepay: isFullRepay,
        repayType: WindType.Principal,
        collateral: collateral,
        loanExpanded,
        quote: undefined,
        isLoopUnwind: !!collateralWithdrawAmount
    });

    const { repay } = useRepay(params);

    const setMaxRepay = useCallback(() => {
        const isMaxRepay = balance >= totalDue; // check that the user has enough balance to repay the entire debt. If so, it is a full repay.
        const maxRepayAmount = isMaxRepay ? totalDue : balance; // if the user has enough balance, use the entire debt amount (this is ignored in the BE due to the max repayment flag). Otherwise, use the balance.

        setIsFullRepay(isMaxRepay);
        setInputAmount(maxRepayAmount);
    }, [balance, totalDue]);

    const handleInputChange = useCallback(
        (amt: number | undefined) => {
            // input amount is rounded in React <NumericFormat/> input and need to compare total due rounded
            const roundedTotalDue = roundAutoToDecimals(totalDue, principalMetadataDecimals);

            const isFullRepayValue = amt === roundedTotalDue;

            setInputAmount(amt);
            setIsFullRepay(isFullRepayValue);
        },
        [totalDue, principalMetadataDecimals]
    );

    const ctaError = useMemo(() => {
        if (balanceLoading) return "Loading";
        if (!balance) return "No available balance";
        if (!effectiveInputAmount) return "Enter an amount";

        if (!isFullRepay && (inputAmount ?? 0) > roundAutoToDecimals(totalDue, principalMetadataDecimals)) {
            return "Exceeds total due";
        }
        if (insufficientBalance) return "Insufficient balance";
        return null;
    }, [
        effectiveInputAmount,
        isFullRepay,
        inputAmount,
        totalDue,
        insufficientBalance,
        balanceLoading,
        balance,
        principalMetadataDecimals
    ]);

    return {
        inputAmount,
        isFullRepay,
        handleInputChange,
        setMaxRepay,
        totalDue,
        effectiveInputAmount,
        principalMetadataDecimals,
        maxRepayable,
        balance,
        balanceLoading,
        insufficientBalance,
        principalMetadata: loanExpanded?.principalMetadata,
        submit: repay,
        ctaError,
        newPrincipalAmount: totalDue - effectiveInputAmount,
        newCollateralAmount: newCollateralAmount
    };
}
